/**
 * utilmate-handoff.service.ts — Theo-side recurring → Utilmate hand-off.
 *
 * Recurring service plans are billed in Utilmate, not through Theo's
 * Stripe pipeline (decision D18 / UD1 in
 * docs/UTILMATE_RECURRING_BILLING_PLAN.md). Because the documented
 * Utilmate API is read-only (no create endpoints), the office sets the
 * recurring service up in Utilmate by hand; Theo tracks that hand-off as
 * a first-class lifecycle on `CustomerService.utilmateHandoff` and
 * (Phase 3) auto-verifies it via the read API.
 *
 * The single-doc writes here need no tenantId filter (per-doc rules govern
 * them). The backfill at the bottom DOES read the strict-rule
 * customer_services collection, so it carries the required tenantId filter.
 *
 * @see docs/UTILMATE_RECURRING_BILLING_PLAN.md §7 Phase 1
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/firebase';
import { getTenantIdSync, tenantIdReadUnion } from '@/lib/tenant';
import type { CustomerService } from '@/types';

/**
 * Fire the server-side verify/sync for one service against Utilmate. Thin
 * wrapper around the syncUtilmateService callable — shared by the Sync button
 * and the auto-sync-on-expand. The page's customer_services onSnapshot picks
 * up the refreshed fields; this just returns the status for inline feedback.
 */
export async function triggerUtilmateSync(
  serviceId: string,
): Promise<{ status: string; message?: string }> {
  const fns = getFunctions();
  const sync = httpsCallable<{ serviceId: string }, { status: string; message?: string }>(
    fns,
    'syncUtilmateService',
  );
  const res = await sync({ serviceId });
  return res.data;
}

/** Whether a service's last sync is older than `maxAgeMs` (or never synced). */
export function isUtilmateSyncStale(
  service: Pick<CustomerService, 'utilmateHandoff'>,
  maxAgeMs = 15 * 60 * 1000,
): boolean {
  const last = service.utilmateHandoff?.lastSyncedAt;
  if (!last) return true;
  const t = new Date(last).getTime();
  if (!Number.isFinite(t)) return true;
  return Date.now() - t > maxAgeMs;
}

type UtilmateHandoff = NonNullable<CustomerService['utilmateHandoff']>;
type UtilmateHandoffStatus = UtilmateHandoff['status'];

const COLLECTION = 'customer_services';

/**
 * Whether a RECURRING service of this type is billed in Utilmate (UD1/UD7).
 *
 * Starlink is excluded — it bills via its own email-parser feeder (D8).
 * Everything else recurring (NBN, and per the stated decision VoIP/others)
 * routes to Utilmate.
 *
 * ⚠️ If a recurring type turns out NOT to bill in Utilmate (e.g. VoIP via
 * Sasboss — see plan §11 Q3), add it to the exclusion list below.
 */
const UTILMATE_EXCLUDED_RECURRING = ['starlink'];

export function billsRecurringInUtilmate(serviceType?: string): boolean {
  if (!serviceType) return false;
  const t = serviceType.toLowerCase();
  return !UTILMATE_EXCLUDED_RECURRING.some((ex) => t.includes(ex));
}

/**
 * The hand-off block stamped on a newly-created recurring Utilmate service.
 * Returned (not written) so creation sites can fold it into their single
 * `addDoc` payload rather than doing a second write.
 */
export function initialUtilmateHandoff(): UtilmateHandoff {
  return { status: 'pending_setup' };
}

/**
 * Office confirms the recurring service has been set up in Utilmate and
 * captures the Utilmate account number (the read-sync join key, UD5).
 * Flips the hand-off to `active`.
 */
export async function markUtilmateSetup(
  serviceId: string,
  details: { accountNumber: string; siteIdentifier?: string },
  by: string,
  byName: string,
): Promise<void> {
  const accountNumber = details.accountNumber.trim();
  if (!serviceId) throw new Error('serviceId is required');
  if (!accountNumber) throw new Error('A Utilmate account number is required');

  const now = new Date().toISOString();
  const update: Record<string, unknown> = {
    'utilmateHandoff.status': 'active' satisfies UtilmateHandoffStatus,
    'utilmateHandoff.accountNumber': accountNumber,
    'utilmateHandoff.setupBy': by,
    'utilmateHandoff.setupByName': byName,
    'utilmateHandoff.setupAt': now,
    'utilmateHandoff.lastSyncError': '', // clear any prior error
    updatedAt: now,
  };
  const site = details.siteIdentifier?.trim();
  if (site) update['utilmateHandoff.siteIdentifier'] = site;

  await updateDoc(doc(db, COLLECTION, serviceId), update);
}

/**
 * Generic status setter for the read-sync / verify flows (Phase 3) and the
 * "not set up" reset path. Only writes the fields provided.
 */
export async function setUtilmateHandoffStatus(
  serviceId: string,
  status: UtilmateHandoffStatus,
  opts: {
    lastSyncedAt?: string;
    lastVerifiedAt?: string;
    lastSyncError?: string;
  } = {},
): Promise<void> {
  if (!serviceId) throw new Error('serviceId is required');
  const update: Record<string, unknown> = {
    'utilmateHandoff.status': status,
    updatedAt: new Date().toISOString(),
  };
  if (opts.lastSyncedAt !== undefined) update['utilmateHandoff.lastSyncedAt'] = opts.lastSyncedAt;
  if (opts.lastVerifiedAt !== undefined) update['utilmateHandoff.lastVerifiedAt'] = opts.lastVerifiedAt;
  if (opts.lastSyncError !== undefined) update['utilmateHandoff.lastSyncError'] = opts.lastSyncError;

  await updateDoc(doc(db, COLLECTION, serviceId), update);
}

// ─── Backfill ────────────────────────────────────────────────────────────────

export interface UtilmateBackfillResult {
  /** Recurring Utilmate-billed services WITHOUT a hand-off (the candidates). */
  scanned: number;
  /** Hand-offs written (0 on a dry run). */
  stamped: number;
  /** Of the candidates, how many had an account number → 'active' (sync-ready). */
  withAccount: number;
  /** Of the candidates, how many had no account number → 'pending_setup'. */
  pendingSetup: number;
  /** Recurring Utilmate-billed services that ALREADY had a hand-off. */
  alreadyHandled: number;
}

/**
 * One-time backfill: stamp `utilmateHandoff` onto EXISTING recurring services
 * that bill in Utilmate but predate the hand-off feature. Auto-fills the
 * Utilmate account number from the linked `Customer.utilmateId` (what the NBN
 * reconciliation populates) or `service.metadata.accountNumber` — services
 * with an account number go straight to 'active' (sync-ready); the rest land
 * at 'pending_setup' for manual confirmation.
 *
 * Admin-run from /home/admin/utilmate. Idempotent — services that already
 * have a hand-off are skipped. Pass { dryRun: true } to preview counts
 * without writing.
 */
export async function backfillUtilmateHandoffs(
  opts: { dryRun?: boolean } = {},
): Promise<UtilmateBackfillResult> {
  const dryRun = opts.dryRun ?? false;
  const tenantId = getTenantIdSync();

  // Strict-rule collection — tenantId filter is mandatory. Single `in`
  // filter only (no composite index needed); everything else filtered
  // in memory.
  const snap = await getDocs(
    query(
      collection(db, COLLECTION),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    ),
  );

  const result: UtilmateBackfillResult = {
    scanned: 0,
    stamped: 0,
    withAccount: 0,
    pendingSetup: 0,
    alreadyHandled: 0,
  };

  // Candidate = recurring + bills in Utilmate.
  const candidates: { id: string; svc: CustomerService }[] = [];
  for (const d of snap.docs) {
    const svc = d.data() as CustomerService;
    if (svc.billingType !== 'recurring') continue;
    if (!billsRecurringInUtilmate(svc.serviceType)) continue;
    if (svc.utilmateHandoff) {
      result.alreadyHandled++;
      continue;
    }
    candidates.push({ id: d.id, svc });
  }
  result.scanned = candidates.length;

  // Resolve account numbers from the linked customers (deduped single-doc
  // gets — governed by per-doc rules, no list-query tenantId concern).
  const customerIds = [
    ...new Set(candidates.map((c) => c.svc.customerId).filter(Boolean) as string[]),
  ];
  const customerUtilmateId = new Map<string, string>();
  await Promise.all(
    customerIds.map(async (id) => {
      try {
        const cs = await getDoc(doc(db, 'customers', id));
        const u = cs.exists() ? (cs.data() as { utilmateId?: unknown }).utilmateId : undefined;
        if (typeof u === 'string' && u.trim()) customerUtilmateId.set(id, u.trim());
      } catch {
        /* best-effort — missing customer just means no auto-fill */
      }
    }),
  );

  const resolveAccount = (svc: CustomerService): string | undefined => {
    const fromCustomer = svc.customerId ? customerUtilmateId.get(svc.customerId) : undefined;
    const fromMeta =
      typeof svc.metadata?.accountNumber === 'string' ? svc.metadata.accountNumber.trim() : '';
    return fromCustomer || fromMeta || undefined;
  };

  const now = new Date().toISOString();
  // writeBatch caps at 500 ops; chunk to be safe.
  const CHUNK = 400;
  for (let i = 0; i < candidates.length; i += CHUNK) {
    const slice = candidates.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    for (const { id, svc } of slice) {
      const account = resolveAccount(svc);
      const handoff: UtilmateHandoff = account
        ? { status: 'active', accountNumber: account }
        : { status: 'pending_setup' };
      if (account) result.withAccount++;
      else result.pendingSetup++;
      if (!dryRun) {
        batch.update(doc(db, COLLECTION, id), {
          utilmateHandoff: handoff,
          updatedAt: now,
        });
      }
    }
    if (!dryRun) {
      await batch.commit();
      result.stamped += slice.length;
    }
  }

  return result;
}
